/**
 * Which layout the widget should wear, as a single source of truth on
 * `<html data-viewport>`.
 *
 * This used to be a plain `@media (max-width: 640px)` in the stylesheets, which
 * is wrong the moment the widget is embedded: the loader sizes its iframe to
 * ~424px, so the media query matched on EVERY desktop and the widget sat in the
 * full-bleed mobile layout — square corners, no launcher (AI-2523). A frame that
 * narrow can't tell a desktop embed from a phone by measuring itself.
 *
 * So the host decides when it can (`set-viewport` over the bridge, sent by
 * `widget.js`, which already measures the real viewport to size the frame), and
 * we fall back to our own viewport when nothing tells us — standalone routes
 * like `/widget-preview`, and `/chat`, which fills its iframe and so measures
 * the host correctly anyway.
 */
const MOBILE = '(max-width: 640px)';

/** Set once the host speaks; from then on its answer wins over our own width. */
let hostSaid: boolean | null = null;

function apply(mobile: boolean): void {
  document.documentElement.dataset.viewport = mobile ? 'mobile' : 'desktop';
}

/** The host's answer. Overrides — and keeps overriding — the local fallback. */
export function setHostViewport(mobile: boolean): void {
  hostSaid = mobile;
  apply(mobile);
}

/**
 * Publish a viewport class immediately and keep it current. Called once at
 * startup so every route has the attribute before first paint; the local
 * listener stays attached but defers to the host once it has spoken.
 */
export function initViewport(): void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    apply(false); // no matchMedia (SSR/jsdom): desktop is the safer default
    return;
  }
  const mq = window.matchMedia(MOBILE);
  const sync = () => {
    if (hostSaid === null) apply(mq.matches);
  };
  sync();
  mq.addEventListener('change', sync);
}
